import { useEffect, useState, useCallback } from 'react';
import {
  MagnifyingGlass, UserPlus, Package, ShareNetwork, Key, EnvelopeSimple, Users, IdentificationCard,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAuth, canEditIt } from '@/auth/AuthContext';
import { itApi, STAFF_STATUS_META } from './itApi';
import StaffProfile from './StaffProfile';
import OnboardingWizard from './OnboardingWizard';

function avatarText(name = '') {
  const n = (name || '').trim();
  if (/^[\x00-\x7F\s]+$/.test(n)) {
    const t = n.split(/\s+/).filter(Boolean);
    return ((t[0]?.[0] || '') + (t[1]?.[0] || '')).toUpperCase() || '?';
  }
  return n.replace(/\s/g, '').slice(0, 2) || '?';
}

const Chip = ({ icon: Icon, n, cls }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md font-medium ${n ? cls : 'bg-zinc-100 text-zinc-400'}`}>
    <Icon size={12} weight="bold" />{n}
  </span>
);

const Stat = ({ label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-zinc-200 shadow-sm px-4 py-3">
    <p className="text-xs text-zinc-500">{label}</p>
    <p className={`text-xl font-bold mt-0.5 ${accent || 'text-zinc-900'}`}>{value}</p>
  </div>
);

export default function StaffLedger() {
  const { user } = useAuth();
  if (!canEditIt(user)) {
    return <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center text-sm text-zinc-500" data-testid="staff-no-access">このページを利用するにはIT編集の権限が必要です。</div>;
  }
  return <Ledger />;
}

function Ledger() {
  const [list, setList] = useState([]);
  const [sharedCount, setSharedCount] = useState(0);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('list'); // list | onboard

  const load = useCallback(() => {
    itApi.staff().then(setList).catch(() => {});
    itApi.sharedEmails().then((d) => setSharedCount(d.length)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = list.filter((s) => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (q.trim()) {
      const t = q.trim().toLowerCase();
      return (s.name || '').toLowerCase().includes(t)
        || (s.department_name || '').toLowerCase().includes(t)
        || (s.code || '').toLowerCase().includes(t);
    }
    return true;
  });

  const active = list.filter((s) => s.status === 'active').length;
  const left = list.filter((s) => s.status === 'left').length;

  const selectStaff = (id) => { setMode('list'); setSelectedId(id); };
  const startOnboard = () => { setSelectedId(null); setMode('onboard'); };
  const onboardDone = (newId) => { setMode('list'); load(); setSelectedId(newId); };

  const showRight = mode === 'onboard' || selectedId;

  return (
    <div className="space-y-5" data-testid="staff-ledger">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">社員IT台帳</h1>
          <p className="text-xs text-zinc-400 mt-0.5">社員ごとに資産・IP・ライセンス・メールを一元管理</p>
        </div>
        <Button data-testid="staff-onboard-btn" onClick={startOnboard} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
          <UserPlus size={18} className="mr-1.5" weight="bold" /> オンボーディング
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="staff-stats">
        <Stat label="社員" value={list.length} testid="staff-stat-total" />
        <Stat label="在籍" value={active} accent="text-emerald-600" testid="staff-stat-active" />
        <Stat label="退職" value={left} accent="text-zinc-500" testid="staff-stat-left" />
        <Stat label="共有メール" value={sharedCount} accent="text-teal-700" testid="staff-stat-shared" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Left: list */}
        <div className={`md:col-span-1 space-y-3 ${showRight ? 'hidden md:block' : ''}`}>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input data-testid="staff-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="氏名・部署で検索" className="h-10 pl-9 rounded-lg" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="staff-status-filter" className="h-10 w-28 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">在籍</SelectItem>
                <SelectItem value="left">退職</SelectItem>
                <SelectItem value="all">すべて</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 md:max-h-[calc(100vh-280px)] md:overflow-y-auto md:pr-1" data-testid="staff-list">
            {filtered.length === 0 ? (
              <div className="bg-white rounded-xl border border-zinc-200 p-10 text-center" data-testid="staff-list-empty">
                <Users size={36} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">該当する社員がいません</p>
              </div>
            ) : filtered.map((s) => {
              const sm = STAFF_STATUS_META[s.status] || STAFF_STATUS_META.active;
              const sel = s.id === selectedId;
              return (
                <button key={s.id} data-testid={`staff-list-item-${s.id}`} onClick={() => selectStaff(s.id)}
                  className={`w-full text-left flex items-center gap-3 p-3.5 bg-white border rounded-xl transition-all duration-200 ${sel ? 'border-teal-700 ring-2 ring-teal-700/30 shadow-md' : 'border-zinc-200 hover:border-teal-700 hover:shadow-md hover:-translate-y-0.5'}`}>
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold shrink-0 ${sel ? 'bg-teal-700 text-white' : 'bg-zinc-100 text-zinc-600'}`}>{avatarText(s.name)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-zinc-800 truncate">{s.name}</span>
                      {s.status === 'left' && <Badge variant="outline" className={`${sm.cls} text-[10px]`}>{sm.label}</Badge>}
                    </div>
                    <p className="text-[11px] text-zinc-400 truncate">{s.department_name || '部署未設定'}</p>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      <Chip icon={Package} n={s.counts.assets} cls="bg-blue-50 text-blue-600" />
                      <Chip icon={ShareNetwork} n={s.counts.ips} cls="bg-violet-50 text-violet-600" />
                      <Chip icon={Key} n={s.counts.licenses} cls="bg-amber-50 text-amber-600" />
                      <Chip icon={EnvelopeSimple} n={s.counts.emails} cls="bg-emerald-50 text-emerald-600" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: profile or wizard */}
        <div className={`md:col-span-2 ${!showRight ? 'hidden md:block' : ''}`}>
          {mode === 'onboard' ? (
            <OnboardingWizard onCancel={() => setMode('list')} onDone={onboardDone} />
          ) : selectedId ? (
            <StaffProfile employeeId={selectedId} staffList={list} onBack={() => setSelectedId(null)} onChanged={load} />
          ) : (
            <div className="hidden md:flex flex-col items-center justify-center h-full min-h-[400px] bg-white rounded-xl border border-dashed border-zinc-200 text-center px-6" data-testid="staff-profile-placeholder">
              <IdentificationCard size={48} weight="light" className="text-zinc-300 mb-3" />
              <p className="text-sm font-semibold text-zinc-600">社員を選択してください</p>
              <p className="text-xs text-zinc-400 mt-1">左の一覧から社員を選ぶと、保有するIT資産をまとめて確認できます</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
